import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { AgGridReact } from 'ag-grid-react';
import { AgGridEvent } from 'ag-grid-community';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, ValueGetterParams, RowDataChangedEvent } from 'ag-grid-community';
import { Button, Label } from 'react-bootstrap';
import { IManualInvoiceLineItem } from '@nkr/store/billing/actions-admin-line-items';
import * as moment from "moment";

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-bootstrap.css';
import { GridWrapper } from './styles';


interface IProps {
    list: IManualInvoiceLineItem[],
    onGridReady: (e: GridReadyEvent) => void
    deleteLineItem: (id: number) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();
        props.onGridReady(e);
    };

    const deleteLineItem = (id: number) => {
        props.deleteLineItem(id);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const formatMoney = (value: any) => {
        let num = Number(value);
        return isNaN(num) ? "$-.--" : `\$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    const onSortChanged = (e: AgGridEvent) => {
        e.api.refreshCells();
    }

    const getTotals = () => {
        return {
            id: 0,
            rank: "",
            username: "",        // was 'usename' (typo)
            centerAlias: "TOTALS:",
            date: null,
            description: "",
            applyToDescription: "",
            isCredit: "",
            amount: _.sumBy(props.list, item => item.amount),
            organType: null,
            actions: "",
        };
    };

    const cols: ColDef[] = [
        {
            colId: 'rank',
            field: 'rank',
            headerName: 'R#',
            headerComponentFramework: (props: IHeaderParams) => <div>R#</div>,
            pinnedRowCellRendererFramework: () => <div></div>,
            sortable: false,
            width: 50,
            valueGetter: (props: ValueGetterParams) => props.node.rowPinned ? '' : props.data.id ? props.node.rowIndex + 1 : "",
        },
        {
            colId: 'username',
            field: 'username',
            headerName: 'AddedBy',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Added by</SortableHeaderComponent>,
            width: 100,
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 150,
        },
        {
            colId: 'date',
            field: 'date',
            headerName: 'Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date</SortableHeaderComponent>,
            pinnedRowCellRenderer: (params: ICellRendererParams) => ``,
            width: 100,
            cellRendererFramework: (props: ICellRendererParams) => {

                if (props.data.date)
                    return <>{moment(props.data.date).format("MM/DD/YYYY")}</>;
                else
                    return <></>;
            },
        },
        {
            colId: 'organType',
            field: 'organType',
            headerName: 'Organ Type',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
            width: 150,
            cellStyle: { textAlign: 'center' },
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IManualInvoiceLineItem;
                if (params.node.rowPinned) return '';
                let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
            }
        },
        {
            colId: 'description',
            field: 'description',
            headerName: 'Description',
            cellClass: 'wrap-cell',
            cellStyle: {
                textAlign: 'left',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: '1.2em',
                maxHeight: '6em',
                overflow: 'hidden'
            },
            valueFormatter: params => {
                const text = params.value || '';
                return text.length > 450 ? text.slice(0, 450) + '...' : text;
            },
            autoHeight: true,
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Description</SortableHeaderComponent>
            ),
            width: 450
        },
        {
            colId: 'applyToDescription',
            field: 'applyToDescription',
            headerName: 'Apply to',
            cellClass: 'wrap-cell',
            cellStyle: {
                textAlign: 'left',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
            },
            autoHeight: true,
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Apply to</SortableHeaderComponent>
            ),
            width: 300
        },
        {
            colId: 'isCredit',
            field: 'isCredit',
            headerName: 'Type',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellStyle: { textAlign: 'center' },
            cellRendererFramework: (props: ICellRendererParams) => {
                if (props.data.id)
                    return <>{props.data.isCredit ? "Credit" : "Debit"}</>;
                else
                    return <></>;
            },
            width: 100,
        },
        {
            colId: 'amount',
            field: 'amount',
            headerName: 'Amount',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>Amount</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                return <>{formatMoney(props.data.amount)}</>;
            },
            width: 100
        },
        {
            colId: 'actions',
            field: 'actions',
            headerName: 'Actions',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
            pinnedRowCellRendererFramework: () => <div></div>,
            sortable: false,
            cellStyle: { overflow: 'visible' },
            width: 120,
            cellRendererFramework: (props: ICellRendererParams) => {
                if (props.data.id)
                    return <Button bsStyle='btn btn-danger' bsSize='xs' onClick={() => deleteLineItem(props.data.id)}>delete</Button>
                else
                    return <div></div>
            }
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const totals = useMemo(() => getTotals(), [props.list]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    sortable: true,
                    cellClass: 'list-cell'
                }}
                columnDefs={columnDefs}
                rowData={props.list}
                headerHeight={30}
                rowHeight={30}
                suppressCellSelection={true}
                enableCellChangeFlash={true}
                enableCellTextSelection={true}
                onSortChanged={onSortChanged}
                pinnedTopRowData={[totals]}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onBodyScroll={onBodyScroll}
                onGridSizeChanged={onGridSizeChanged}
                onRowDataChanged={onRowDataChanged}
                rowStyle={{ overflow: 'visible' }}
            />
        </GridWrapper>
    );
}

export default Grid;